import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Gift, Search, AlertTriangle, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { playSfx } from "@/lib/celebrate";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserBits";
import { ALLIN_GIFTS, useGiftCfg } from "./allinGifts";
import { GiftAnimation } from "./GiftAnimation";


function MemberPicker({ onPick }) {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setList([]); return undefined; }
    const t = setTimeout(() => api.get("/community/members", { params: { q } }).then(({ data }) => setList(data.filter((u) => u.user_id !== user?.user_id).slice(0, 6))).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, user?.user_id]);
  return (
    <div className="space-y-2">
      <div className="relative"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mitglied suchen …" data-testid="allin-search" className="w-full h-10 pl-9 pr-3 rounded-xl border border-pink-100 bg-white text-sm outline-none focus:ring-2 focus:ring-rose-200" />
      </div>
      {list.map((u) => (
        <button key={u.user_id} onClick={() => onPick(u)} data-testid={`allin-pick-${u.user_id}`} className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-rose-50 text-left">
          <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-8 h-8" text="text-xs" noStory /><span className="text-sm font-semibold truncate">{u.name}</span>
        </button>
      ))}
    </div>
  );
}

export function AllInDialog({ to: fixedTo, onClose }) {
  const { user, refreshUser } = useAuth();
  const [to, setTo] = useState(fixedTo || null);
  const [gift, setGift] = useState("kiss");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(null);
  const pts = user?.points ?? 0;
  const { cost: MIN } = useGiftCfg();
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/allin/send", { to: to.user_id, gift });
      refreshUser?.();
      setShown({ gift, lost: data.lost, reward: data.reward });
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (shown) return <GiftAnimation gift={shown.gift} fromName={user?.name} toName={to.name} note={`${to.name} bekommt +${shown.reward} Punkte · dich hat es ${shown.lost.toLocaleString("de-DE")} Punkte gekostet`} onClose={onClose} />;
  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="allin-dialog">
        <div className="flex items-center justify-between"><h3 className="font-display text-xl font-extrabold">Geschenk verschicken</h3><button onClick={onClose} data-testid="allin-dialog-close" className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5" /></button></div>
        <Warning />
        {to ? (
          <div className="flex items-center gap-3 p-3 rounded-2xl bg-rose-50" data-testid="allin-recipient">
            <UserAvatar userId={to.user_id} name={to.name} picture={to.picture} size="w-9 h-9" text="text-sm" noStory /><span className="font-bold flex-1 truncate">an {to.name}</span>
            {!fixedTo && <button onClick={() => setTo(null)} className="text-xs font-semibold text-rose-600">ändern</button>}
          </div>
        ) : <MemberPicker onPick={setTo} />}
        <div className="grid grid-cols-3 gap-2">
          {ALLIN_GIFTS.map((g) => (
            <button key={g.id} onClick={() => setGift(g.id)} data-testid={`allin-gift-${g.id}`} aria-pressed={gift === g.id}
              className={`rounded-2xl p-3 flex flex-col items-center gap-1 border-2 transition-[transform,border-color] active:scale-95 ${gift === g.id ? "border-rose-500 bg-rose-50 -translate-y-0.5" : "border-pink-100 bg-white"}`}>
              <span className={`text-4xl ${gift === g.id ? "gift-heartbeat" : ""}`}>{g.emoji}</span><span className="text-[11px] font-bold leading-tight text-center">{g.name}</span>
            </button>
          ))}
        </div>
        <label className="flex items-start gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
          <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="allin-confirm" className="mt-1 accent-rose-600 w-4 h-4" />
          Ich weiß, dass mich das Geschenk {MIN} Punkte kostet (du hast {pts.toLocaleString("de-DE")}).
        </label>
        <button onClick={send} disabled={!to || !ok || busy || pts < MIN} data-testid="allin-send"
          className="w-full h-12 rounded-full pink-gradient text-white font-extrabold shadow-lg shadow-rose-200 disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Gift className="w-5 h-5" />} {pts < MIN ? `Mindestens ${MIN} Punkte nötig` : `Für ${MIN} Punkte schenken`}
        </button>
      </div>
    </div>
  );
}

function Warning() {
  const { cost, reward } = useGiftCfg();
  return (
    <div className="rounded-2xl bg-red-50 border-2 border-red-200 p-3 flex gap-3" data-testid="allin-warning">
      <AlertTriangle className="w-6 h-6 text-red-600 shrink-0" />
      <p className="text-sm text-red-800"><b>Achtung: Jedes Geschenk kostet dich {cost} Punkte!</b> Sie werden sofort von deinem Guthaben abgezogen. Der Beschenkte bekommt {reward} Punkte.</p>
    </div>
  );
}

export function AllInSection() {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-3xl p-5 bg-gradient-to-br from-rose-50 via-white to-amber-50 border border-pink-100 shadow-sm space-y-4" data-testid="allin-section">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-2xl pink-gradient flex items-center justify-center gift-glow"><Gift className="w-6 h-6 text-white" /></span>
        <div><h2 className="font-display text-xl font-extrabold">Geschenke verschicken</h2><p className="text-sm text-slate-500">Mach jemandem eine riesige Freude – mit einem leuchtenden Geschenk.</p></div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {ALLIN_GIFTS.map((g, i) => (
          <div key={g.id} className={`rounded-2xl p-3 text-center bg-gradient-to-br ${g.tone} text-white shadow-md`} style={{ animationDelay: `${i * 0.2}s` }}>
            <span className="block text-4xl gift-float" style={{ animationDelay: `${i * 0.4}s` }}>{g.emoji}</span><span className="block text-[11px] font-extrabold mt-1">{g.name}</span>
          </div>
        ))}
      </div>
      <Warning />
      <button onClick={() => setOpen(true)} data-testid="allin-open" className="w-full h-12 rounded-full pink-gradient text-white font-extrabold shadow-lg shadow-rose-200 flex items-center justify-center gap-2"><Gift className="w-5 h-5" /> Geschenk verschicken</button>
      {open && <AllInDialog onClose={() => setOpen(false)} />}
    </section>
  );
}

export function AllInProfileButton({ p, className }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="profile-allin-btn" className={className}><Gift className="w-4 h-4" /> Schenken</button>
      {open && <AllInDialog to={{ user_id: p.user_id, name: p.name, picture: p.picture }} onClose={() => setOpen(false)} />}
    </>
  );
}

export function AllInFeedCard() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/allin/recent").then(({ data }) => setList(data)).catch(() => {}); }, []);
  const g = list[0];
  if (!g) return null;
  const meta = ALLIN_GIFTS.find((x) => x.id === g.gift) || ALLIN_GIFTS[0];
  return (
    <Link to="/spiele" data-testid="allin-feed-card" className={`relative block overflow-hidden rounded-3xl p-5 text-white bg-gradient-to-br ${meta.tone} shadow-lg shadow-rose-200 gift-card-glow`}>
      <span className="absolute -right-4 -top-4 text-8xl opacity-30 gift-float" aria-hidden>{meta.emoji}</span>
      <p className="text-[11px] font-extrabold tracking-[0.3em] uppercase text-white/80">Geschenk</p>
      <p className="mt-1 font-display text-lg font-extrabold leading-snug pr-14" data-testid="allin-feed-text">{g.from_name} hat {g.to_name} {g.label} geschenkt</p>
      <p className="mt-2 text-xs font-semibold text-white/85">{g.from_name} hat dafür {g.cost} Punkte eingesetzt · Jetzt selbst schenken →</p>
      {g.thanked && <p className="mt-2 inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-white/25 text-xs font-extrabold" data-testid="allin-feed-thanked">{g.to_name} hat sich bedankt 💖</p>}
      {g.thank_msg && <p className="mt-2 text-sm font-semibold italic text-white break-words" data-testid="allin-feed-thank-msg">„{g.thank_msg}“</p>}
    </Link>
  );
}

export function ThankButton({ gid, onDone, onCompose, big = false }) {
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const open = (e) => { stop(e); setState("compose"); onCompose?.(); };
  const thank = async (e) => {
    stop(e);
    setState("busy");
    try { await api.post(`/allin/${gid}/thank`, { message: msg.trim() }); setState("done"); toast.success("Danke gesendet 💖"); onDone?.(); }
    catch (err) { if (err?.response?.status === 409) { setState("done"); toast(formatErr(err)); } else { setState("compose"); toast.error(formatErr(err)); } }
  };
  if (state === "done") return <span data-testid={`allin-thanked-${gid}`} className={`shrink-0 font-bold text-rose-500 ${big ? "text-base" : "text-xs"}`}>Danke gesendet 💖</span>;
  if (state === "compose" || state === "busy") return (
    <div onClick={stop} className={`flex flex-col gap-2 ${big ? "w-full max-w-sm" : "w-full mt-2"}`} data-testid={`allin-thank-compose-${gid}`}>
      <div className="relative">
        <input autoFocus value={msg} maxLength={80} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && thank(e)} placeholder="Ein lieber Satz (freiwillig) …" data-testid={`allin-thank-input-${gid}`}
          className="w-full h-11 pl-4 pr-14 rounded-full bg-white text-slate-800 text-sm font-semibold outline-none ring-2 ring-rose-200 focus:ring-rose-400" />
        <span className="absolute right-4 top-3 text-[11px] font-bold text-slate-400 tabular-nums">{msg.length}/80</span>
      </div>
      <button onClick={thank} disabled={state === "busy"} data-testid={`allin-thank-send-${gid}`} className="h-11 rounded-full pink-gradient text-white font-extrabold shadow-md shadow-rose-300/50 active:scale-95 transition-transform disabled:opacity-60">
        {state === "busy" ? "Wird geprüft …" : "Danke senden 💖"}
      </button>
    </div>
  );
  return (
    <button onClick={open} data-testid={`allin-thank-${gid}`}
      className={`shrink-0 rounded-full font-extrabold text-white pink-gradient shadow-md shadow-rose-300/50 active:scale-95 transition-transform ${big ? "h-12 px-8 text-base" : "h-8 px-3 text-xs"}`}>
      Danke 💖
    </button>
  );
}

function ThankPop({ g, onDone }) {
  useEffect(() => { playSfx("thanks"); const t = setTimeout(onDone, g.thank_msg ? 6000 : 3500); return () => clearTimeout(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return createPortal(
    <div className="fixed left-1/2 -translate-x-1/2 bottom-32 lg:bottom-10 z-[95]" data-testid="allin-thanks-pop" onClick={onDone}>
      <div className="relative flex items-center gap-3 pl-3 pr-5 py-3 w-[min(22rem,calc(100vw-2rem))] rounded-3xl bg-white shadow-2xl shadow-rose-300/60 ring-2 ring-rose-200 gift-pop">
        <span className="relative w-11 h-11 shrink-0 rounded-full pink-gradient flex items-center justify-center text-2xl gift-heartbeat">💖</span>
        {["💗", "💕", "✨"].map((e, i) => <span key={e} className="absolute -top-2 text-lg gift-rise" style={{ left: `${20 + i * 25}%`, bottom: "auto", animationDuration: "2.2s", animationDelay: `${i * 0.3}s` }}>{e}</span>)}
        <div className="min-w-0 flex-1">
        <p className="text-sm font-extrabold text-slate-800" data-testid="allin-thanks-text">{g.to_name} sagt Danke für {g.label.replace(/^einen /, "deinen ").replace(/^ein /, "dein ")} 💖</p>
        {g.thank_msg && <p className="mt-0.5 text-sm text-slate-600 italic break-words" data-testid="allin-thanks-msg">„{g.thank_msg}“</p>}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function AllInInbox() {
  const { user, refreshUser } = useAuth();
  const [queue, setQueue] = useState([]);
  const [thanks, setThanks] = useState([]);
  useEffect(() => {
    if (!user) return undefined;
    const me = user.user_id;
    const load = async () => {
      try {
        const [{ data: mine }, { data: pub }, { data: thx }] = await Promise.all([api.get("/allin/pending"), api.get("/allin/recent"), api.get("/allin/thanks-pending")]);
        if (thx.length) setThanks((q) => [...q, ...thx.filter((g) => !q.some((x) => x.id === g.id))]);
        const stamp = localStorage.getItem(SEEN_KEY);
        const newest = pub[0]?.created_at || new Date(0).toISOString();
        if (!stamp) localStorage.setItem(SEEN_KEY, newest);
        const fresh = stamp ? pub.filter((g) => g.created_at > stamp && g.to_id !== me && g.from_id !== me).reverse() : [];
        if (stamp && pub.length && newest > stamp) localStorage.setItem(SEEN_KEY, newest);
        const add = [...mine.map((g) => ({ ...g, mine: true })), ...fresh];
        if (add.length) setQueue((q) => [...q, ...add.filter((g) => !q.some((x) => x.id === g.id))]);
      } catch { /* offline */ }
    };
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [hold, setHold] = useState(false);
  const cur = queue[0];
  const thx = thanks[0];
  const thxDone = () => { api.post(`/allin/${thx.id}/thank-seen`).catch(() => {}); setThanks((q) => q.slice(1)); };
  if (!cur) return thx ? <ThankPop key={thx.id} g={thx} onDone={thxDone} /> : null;
  const close = () => {
    if (cur.mine) { api.post(`/allin/${cur.id}/seen`).catch(() => {}); refreshUser?.(); }
    setHold(false);
    setQueue((q) => (q[0]?.id === cur.id ? q.slice(1) : q));
  };
  return <GiftAnimation key={cur.id} gift={cur.gift} fromName={cur.from_name} toName={cur.mine ? "dir" : cur.to_name} note={cur.mine ? `+${cur.reward} Punkte für dich 🎉` : `${cur.from_name} hat dafür ${cur.cost} Punkte eingesetzt`} hold={hold}
    action={cur.mine && <ThankButton gid={cur.id} big onCompose={() => setHold(true)} onDone={() => setTimeout(close, 900)} />} onClose={close} />;
}

const SEEN_KEY = "itz_allin_seen";
